import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { CAMPAIGN_TONES } from '#/entities/campaign'
import type { CampaignContactRow } from '#/entities/campaign'
import { phoneToDialable } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import type { DataTableColumn } from '#/shared/ui/data-table'
import { CallablePhone } from '#/shared/ui/phone-number'

import type { CampaignContactRowActions } from '../model/use-campaign-contact-row-actions'
import {
  hasQuoteCode,
  useOpenCampaignQuote,
} from '../model/use-open-campaign-quote'
import { CampaignContactRowMenu } from './campaign-contact-row-menu'

function contactColumn(t: TFunction): DataTableColumn<CampaignContactRow> {
  return {
    key: 'contact',
    header: t('campaigns.contact.columns.contact'),
    className: 'w-49',
    render: (row) => (
      <span className="flex flex-col gap-0.75">
        <span className="text-sm font-medium text-gray-900">{row.name}</span>
        {phoneToDialable(row.phone) == null ? (
          <span className="text-xs text-gray-500">{row.phone}</span>
        ) : (
          <CallablePhone
            value={row.phone}
            className="text-xs text-gray-500"
            smsTarget={{
              phone: row.phone,
              name: row.name,
              shipmentId: row.shipmentId,
              customerId: row.customerId,
            }}
          />
        )}
        <span className="truncate text-xs text-gray-300">{row.email}</span>
      </span>
    ),
  }
}

/** The quote's code opens its shipment — see `useOpenCampaignQuote`. */
function quoteColumn(
  t: TFunction,
  onOpenQuote: (row: CampaignContactRow) => void,
): DataTableColumn<CampaignContactRow> {
  return {
    key: 'quote',
    header: t('campaigns.contact.columns.quote'),
    className: 'w-59',
    render: (row) => (
      <span className="flex flex-col gap-0.75 text-xs">
        <span className="flex items-center gap-2">
          {!hasQuoteCode(row) ? (
            <span className="font-medium text-campaign-running">
              {row.quote.code}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onOpenQuote(row)}
              className="cursor-pointer font-medium text-campaign-running hover:underline"
            >
              {row.quote.code}
            </button>
          )}
          <span className="font-medium text-gray-900">{row.quote.vehicle}</span>
        </span>
        <span className="text-gray-500">{row.quote.route}</span>
        <span className="text-gray-300">{row.quote.meta}</span>
      </span>
    ),
  }
}

/** The quote's state under its price ("Open quote", "Booked") opens the same
 *  shipment the quote code does. */
function tariffColumn(
  t: TFunction,
  onOpenQuote: (row: CampaignContactRow) => void,
): DataTableColumn<CampaignContactRow> {
  return {
    key: 'tariff',
    header: t('campaigns.contact.columns.tariff'),
    className: 'w-23 text-right',
    render: (row) => (
      <span className="flex flex-col items-end gap-0.75">
        <span className="text-sm font-medium text-gray-900">
          {row.tariff.amount}
        </span>
        {hasQuoteCode(row) ? (
          <button
            type="button"
            onClick={() => onOpenQuote(row)}
            className={cn(
              'cursor-pointer text-xs hover:underline',
              CAMPAIGN_TONES[row.tariff.statusTone],
            )}
          >
            {row.tariff.status}
          </button>
        ) : (
          <span
            className={cn('text-xs', CAMPAIGN_TONES[row.tariff.statusTone])}
          >
            {row.tariff.status}
          </span>
        )}
      </span>
    ),
  }
}

function stepColumn(t: TFunction): DataTableColumn<CampaignContactRow> {
  return {
    key: 'step',
    header: t('campaigns.contact.columns.step'),
    className: 'w-24',
    render: (row) => (
      <span className="flex flex-col gap-0.75 text-xs">
        <span className="font-medium text-gray-900">{row.step.label}</span>
        <span className="text-gray-300">{row.step.detail}</span>
      </span>
    ),
  }
}

function activityColumn(t: TFunction): DataTableColumn<CampaignContactRow> {
  return {
    key: 'activity',
    header: t('campaigns.contact.columns.lastActivity'),
    className: 'min-w-52',
    render: (row) => (
      <span className="flex flex-col gap-0.75 text-xs">
        <span
          className={cn(
            'whitespace-normal',
            row.activity.failed ? 'text-campaign-stopped' : 'text-gray-900',
          )}
        >
          {row.activity.headline}
        </span>
        <span className="text-gray-300">{row.activity.timestamp}</span>
        {row.activity.answerState != null && (
          <span
            className={
              row.activity.awaiting
                ? 'font-medium text-campaign-attention'
                : 'text-warm-500'
            }
          >
            {row.activity.answerState}
          </span>
        )}
      </span>
    ),
  }
}

function outcomeColumn(t: TFunction): DataTableColumn<CampaignContactRow> {
  return {
    key: 'outcome',
    header: t('campaigns.contact.columns.outcome'),
    className: 'w-35',
    render: (row) => (
      <span
        className={cn('text-xs font-medium', CAMPAIGN_TONES[row.outcomeTone])}
      >
        {row.outcome}
      </span>
    ),
  }
}

function actionsColumn(
  actions: CampaignContactRowActions,
): DataTableColumn<CampaignContactRow> {
  return {
    key: 'actions',
    header: '',
    className: 'w-10',
    render: (row) => (
      <div onClick={(event) => event.stopPropagation()}>
        <CampaignContactRowMenu row={row} actions={actions} />
      </div>
    ),
  }
}

export function useCampaignContactColumns(
  actions: CampaignContactRowActions,
): DataTableColumn<CampaignContactRow>[] {
  const { t } = useTranslation()
  const openQuote = useOpenCampaignQuote()
  return useMemo(
    () => [
      contactColumn(t),
      quoteColumn(t, openQuote),
      tariffColumn(t, openQuote),
      stepColumn(t),
      activityColumn(t),
      outcomeColumn(t),
      actionsColumn(actions),
    ],
    [t, openQuote, actions],
  )
}
